import { useEffect, useState, useRef } from "react";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError, UPLOADS_BASE } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { SignaturePad } from "../../components/SignaturePad";
import { Plus, Trash2, Upload, Download, ChevronRight, ChevronLeft, Check, X as XIcon, Ban, FileDown, CalendarClock, Wallet } from "lucide-react";
import { toast } from "sonner";
import { FeuilleTemps, currentMois } from "../../components/FeuilleTemps";
import { TYPE_CONGE_LABELS, TYPE_DEMANDE_SALAIRE_LABELS, StatusBadge } from "../profil/ProfilPage";

function fmtDateTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("fr-FR"); } catch { return iso; }
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
}

function DecisionDialog({ target, onClose, onDecide }) {
  const sigRef = useRef(null);
  const [motif, setMotif] = useState("");
  const [saving, setSaving] = useState(false);

  const accept = async () => {
    const signature_data = sigRef.current?.toDataURL() || "";
    if (!signature_data) { toast.error("Votre signature est requise pour accepter"); return; }
    setSaving(true);
    try { await onDecide("acceptee", signature_data, ""); } finally { setSaving(false); }
  };
  const refuse = async () => {
    setSaving(true);
    try { await onDecide("refusee", "", motif); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="conges-decision-dialog">
        <DialogHeader><DialogTitle>Traiter la demande de {target?.prenom} {target?.nom}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1">Motif du refus (si vous refusez)</label>
            <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} data-testid="conges-motif-refus" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Signature (requise pour accepter)</label>
            <SignaturePad ref={sigRef} testId="conges-decision-signature-pad" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="gap-1" disabled={saving} onClick={refuse} data-testid="conges-refuse-button">
            <XIcon size={14} /> Refuser
          </Button>
          <Button className="gap-1" disabled={saving} onClick={accept} data-testid="conges-accept-button">
            <Check size={14} /> Accepter et signer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AbsenceApprovalTab() {
  const [rows, setRows] = useState([]);
  const [target, setTarget] = useState(null);

  const load = async () => {
    const { data } = await api.get("/profil/absences/all");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const decide = async (action, signature_data, motif_refus) => {
    try {
      await api.patch(`/profil/absences/${target.id}/decision`, { action, signature_data, motif_refus });
      toast.success(action === "acceptee" ? "Absence acceptée" : "Absence refusée");
      setTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const cancel = async (row) => {
    if (!window.confirm("Annuler cette demande d'absence déjà acceptée ?")) return;
    try {
      await api.post(`/profil/absences/${row.id}/annuler`);
      toast.success("Demande d'absence annulée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const pending = rows.filter((r) => r.status === "en_attente");
  const others = rows.filter((r) => r.status !== "en_attente");

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-semibold mb-2">En attente ({pending.length})</p>
        {pending.length === 0 && <p className="text-sm text-muted-foreground">Aucune demande en attente.</p>}
        <div className="space-y-3" data-testid="absence-approval-pending-list">
          {pending.map((r) => (
            <Card key={r.id} data-testid={`absence-approval-row-${r.id}`}>
              <CardContent className="p-4 space-y-1">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="font-medium text-sm">{r.prenom} {r.nom} — {r.magasin}</p>
                  <p className="text-xs text-muted-foreground">{fmtDateTime(r.date_demande)}</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  {TYPE_CONGE_LABELS[r.type_conge]}{r.type_conge === "special" && r.type_conge_special ? ` — ${r.type_conge_special}` : ""} · du {fmtDate(r.date_debut)} au {fmtDate(r.date_fin)} · {r.telephone}
                </p>
                <div className="pt-1 flex items-center gap-2">
                  <Button size="sm" onClick={() => setTarget(r)} data-testid={`absence-approval-decide-${r.id}`}>Traiter</Button>
                  <a href={`${api.defaults.baseURL}/profil/absences/${r.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`absence-approval-pdf-${r.id}`}>
                    <Button size="sm" variant="outline" className="gap-1.5"><FileDown size={13} /> PDF</Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      {others.length > 0 && (
        <div>
          <p className="text-sm font-semibold mb-2">Historique</p>
          <div className="space-y-2" data-testid="absence-approval-history-list">
            {others.map((r) => (
              <Card key={r.id}>
                <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-sm">{r.prenom} {r.nom} — {TYPE_CONGE_LABELS[r.type_conge]} du {fmtDate(r.date_debut)} au {fmtDate(r.date_fin)}</p>
                    {r.status === "acceptee" && <p className="text-xs text-muted-foreground">Acceptée par {r.decision_by_nom}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={r.status} />
                    <a href={`${api.defaults.baseURL}/profil/absences/${r.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`absence-approval-history-pdf-${r.id}`} title="PDF">
                      <Button size="sm" variant="outline" className="gap-1"><FileDown size={13} /></Button>
                    </a>
                    {r.status === "acceptee" && (
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => cancel(r)} data-testid={`absence-approval-cancel-${r.id}`}>
                        <Ban size={13} /> Annuler
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
      <DecisionDialog target={target} onClose={() => setTarget(null)} onDecide={decide} />
    </div>
  );
}

function AvanceApprovalTab() {
  const [rows, setRows] = useState([]);
  const [target, setTarget] = useState(null);

  const load = async () => {
    const { data } = await api.get("/profil/avances/all");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const decide = async (action, signature_data, motif_refus) => {
    try {
      await api.patch(`/profil/avances/${target.id}/decision`, { action, signature_data, motif_refus });
      toast.success(action === "acceptee" ? "Demande acceptée" : "Demande refusée");
      setTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const cancel = async (row) => {
    if (!window.confirm("Annuler cette demande déjà acceptée ?")) return;
    try {
      await api.post(`/profil/avances/${row.id}/annuler`);
      toast.success("Demande annulée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const pending = rows.filter((r) => r.status === "en_attente");
  const others = rows.filter((r) => r.status !== "en_attente");

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-semibold mb-2">En attente ({pending.length})</p>
        {pending.length === 0 && <p className="text-sm text-muted-foreground">Aucune demande en attente.</p>}
        <div className="space-y-3" data-testid="avance-approval-pending-list">
          {pending.map((r) => (
            <Card key={r.id} data-testid={`avance-approval-row-${r.id}`}>
              <CardContent className="p-4 space-y-1">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="font-medium text-sm">{r.prenom} {r.nom} — {r.magasin}</p>
                  <p className="text-xs text-muted-foreground">{fmtDateTime(r.date_demande)}</p>
                </div>
                <p className="text-xs text-muted-foreground">{TYPE_DEMANDE_SALAIRE_LABELS[r.type_demande]} · {r.montant} € · {r.telephone}</p>
                <div className="pt-1 flex items-center gap-2">
                  <Button size="sm" onClick={() => setTarget(r)} data-testid={`avance-approval-decide-${r.id}`}>Traiter</Button>
                  <a href={`${api.defaults.baseURL}/profil/avances/${r.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`avance-approval-pdf-${r.id}`}>
                    <Button size="sm" variant="outline" className="gap-1.5"><FileDown size={13} /> PDF</Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      {others.length > 0 && (
        <div>
          <p className="text-sm font-semibold mb-2">Historique</p>
          <div className="space-y-2" data-testid="avance-approval-history-list">
            {others.map((r) => {
              const deadline = r.cancel_deadline ? new Date(r.cancel_deadline) : null;
              const canCancel = r.status === "acceptee" && (!deadline || deadline > new Date());
              return (
                <Card key={r.id}>
                  <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="text-sm">{r.prenom} {r.nom} — {TYPE_DEMANDE_SALAIRE_LABELS[r.type_demande]} · {r.montant} €</p>
                      {r.status === "acceptee" && (
                        <p className="text-xs text-muted-foreground">
                          Acceptée par {r.decision_by_nom} — annulable jusqu'au {fmtDateTime(r.cancel_deadline)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={r.status} />
                      <a href={`${api.defaults.baseURL}/profil/avances/${r.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`avance-approval-history-pdf-${r.id}`} title="PDF">
                        <Button size="sm" variant="outline" className="gap-1"><FileDown size={13} /></Button>
                      </a>
                      {canCancel && (
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => cancel(r)} data-testid={`avance-approval-cancel-${r.id}`}>
                          <Ban size={13} /> Annuler
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
      <DecisionDialog target={target} onClose={() => setTarget(null)} onDecide={decide} />
    </div>
  );
}

function PointagesTab({ users }) {
  const [userId, setUserId] = useState("");
  const [mois, setMois] = useState(currentMois());
  const [feuille, setFeuille] = useState(null);
  const load = () => {
    if (!userId) return;
    api.get("/pointage/feuille", { params: { user_id: userId, mois } }).then((r) => setFeuille(r.data)).catch((e) => { setFeuille(null); toast.error(formatApiError(e.response?.data?.detail)); });
  };
  useEffect(() => { setFeuille(null); load(); }, [userId, mois]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3" data-testid="pointages-tab">
      <div className="max-w-xs">
        <Select value={userId} onValueChange={setUserId}>
          <SelectTrigger data-testid="pointages-user-select"><SelectValue placeholder="Choisir un employé…" /></SelectTrigger>
          <SelectContent>
            {users.map((u) => <SelectItem key={u.id || u._id} value={u.id || u._id}>{`${u.prenom || ""} ${u.nom || ""}`.trim()}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {userId ? <FeuilleTemps feuille={feuille} mois={mois} onMois={setMois} manager userId={userId} onChanged={load} />
        : <p className="text-sm text-muted-foreground">Sélectionnez un employé pour voir et gérer sa feuille de temps.</p>}
    </div>
  );
}

export default function PersonnelPage() {
  const [subTab, setSubTab] = useState("calendrier");
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, []);

  return (
    <Layout title="Gestion du personnel">
      <div>
        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            ["calendrier", "Calendrier"], ["pointages", "Pointages"], ["commissions", "Commissions"], ["objectifs", "Objectifs de vente"],
            ["dossiers", "Dossiers employés"], ["absences", "Demandes d'absence"], ["avances", "Acomptes / Avances"],
            ["paie", "Export paie"],
          ].map(([key, label]) => (
            <Button
              key={key} size="sm" variant={subTab === key ? "default" : "outline"}
              onClick={() => setSubTab(key)} data-testid={`personnel-subtab-${key}`}
            >
              {label}
            </Button>
          ))}
        </div>
        {subTab === "calendrier" && <CalendrierTab users={users} />}
        {subTab === "pointages" && <PointagesTab users={users} />}
        {subTab === "commissions" && <CommissionsTab users={users} />}
        {subTab === "objectifs" && <ObjectifsTab users={users} />}
        {subTab === "dossiers" && <DossiersEmployesTab users={users} />}
        {subTab === "absences" && <AbsenceApprovalTab />}
        {subTab === "avances" && <AvanceApprovalTab />}
        {subTab === "paie" && <PaieTab />}
      </div>
    </Layout>
  );
}


const STATUT_COLORS = {
  present: "bg-emerald-500",
  prevu: "bg-sky-400",
  absent_non_pointe: "bg-red-500",
};
const CONGE_COLORS = { paye: "bg-blue-500", sans_solde: "bg-slate-400", rtt: "bg-purple-500", special: "bg-orange-500" };
const STATUT_LABELS = {
  present: "Présent", prevu: "Prévu (pas encore travaillé)", absent_non_pointe: "Absent — non pointé",
  conge: "En congé", repos: "Repos (rien de prévu)",
};
const WEEKDAY_LETTERS = ["L", "M", "M", "J", "V", "S", "D"];
const JOURS_SEMAINE = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const JOURS_LABELS = { lundi: "Lundi", mardi: "Mardi", mercredi: "Mercredi", jeudi: "Jeudi", vendredi: "Vendredi", samedi: "Samedi", dimanche: "Dimanche" };

function JourDetailDialog({ cell, onClose, onChanged }) {
  const { user, jour, info } = cell;
  const [editing, setEditing] = useState(null); // objet pointage, ou "new"
  const [heureArrivee, setHeureArrivee] = useState("");
  const [heureDepart, setHeureDepart] = useState("");
  const [finLendemain, setFinLendemain] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const openEdit = (p) => {
    setEditing(p || "new");
    setHeureArrivee(p ? p.heure_arrivee.slice(11, 16) : (info.theorique?.heure_debut || "09:00"));
    setHeureDepart(p?.heure_depart ? p.heure_depart.slice(11, 16) : (info.theorique?.heure_fin || ""));
    // Détecte si un pointage déjà enregistré se termine un autre jour calendaire
    // (travail de nuit) pour précocher la case en conséquence.
    setFinLendemain(!!(p?.heure_depart && p.heure_depart.slice(0, 10) !== p.heure_arrivee.slice(0, 10)));
  };

  const submit = async () => {
    if (!heureArrivee) { toast.error("L'heure d'arrivée est obligatoire"); return; }
    setSubmitting(true);
    try {
      const jourDepart = finLendemain ? new Date(new Date(jour).getTime() + 86400000).toISOString().slice(0, 10) : jour;
      const heure_arrivee = `${jour}T${heureArrivee}:00`;
      const heure_depart = heureDepart ? `${jourDepart}T${heureDepart}:00` : null;
      if (editing === "new") {
        await api.post("/pointage/manuel", { user_id: user.id, heure_arrivee, heure_depart });
      } else {
        await api.patch(`/pointage/${editing.id}`, { heure_arrivee, heure_depart });
      }
      toast.success("Pointage enregistré");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const deletePointage = async (p) => {
    if (!window.confirm("Supprimer ce pointage ?")) return;
    try {
      await api.delete(`/pointage/${p.id}`);
      toast.success("Pointage supprimé");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="jour-detail-dialog">
        <DialogHeader>
          <DialogTitle>{user.prenom} {user.nom} — {new Date(jour).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm">
            <span className="text-muted-foreground">Statut : </span>
            <strong>{STATUT_LABELS[info.statut]}</strong>
            {info.conge && ` — ${TYPE_CONGE_LABELS[info.conge.type_conge] || info.conge.type_conge}`}
          </p>
          <p className="text-sm">
            <span className="text-muted-foreground">Horaire théorique : </span>
            {info.theorique ? `${info.theorique.heure_debut} à ${info.theorique.heure_fin}` : "Aucun (repos)"}
          </p>

          {info.pointages.length === 0 && <p className="text-sm text-muted-foreground">Aucun pointage ce jour-là.</p>}
          <div className="space-y-2">
            {info.pointages.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border border-border p-2 text-sm" data-testid={`jour-detail-pointage-${p.id}`}>
                <span>
                  {p.heure_arrivee.slice(11, 16)} – {p.heure_depart ? p.heure_depart.slice(11, 16) : "en cours"}
                  {p.duree_minutes != null && ` (${Math.floor(p.duree_minutes / 60)}h${String(p.duree_minutes % 60).padStart(2, "0")})`}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`jour-detail-edit-${p.id}`}>Modifier</Button>
                  <button onClick={() => deletePointage(p)} className="text-destructive" title="Supprimer ce pointage" data-testid={`jour-detail-delete-${p.id}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {editing ? (
            <div className="space-y-2 rounded-lg border border-border p-3">
              <p className="text-xs font-medium">{editing === "new" ? "Nouveau pointage (saisie manuelle)" : "Modifier le pointage"}</p>
              <div className="grid grid-cols-2 gap-2">
                <Input type="time" value={heureArrivee} onChange={(e) => setHeureArrivee(e.target.value)} data-testid="jour-detail-arrivee" />
                <Input type="time" value={heureDepart} onChange={(e) => setHeureDepart(e.target.value)} data-testid="jour-detail-depart" />
              </div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <Checkbox checked={finLendemain} onCheckedChange={(v) => setFinLendemain(!!v)} data-testid="jour-detail-fin-lendemain" />
                Se termine le lendemain (travail de nuit)
              </label>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Annuler</Button>
                <Button size="sm" onClick={submit} disabled={submitting} data-testid="jour-detail-submit">Enregistrer</Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openEdit(null)} data-testid="jour-detail-add-pointage">
              <Plus size={13} />Pointer / renseigner manuellement
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RenseignerEmployeDialog({ users, onClose, onSaved }) {
  const [mode, setMode] = useState("stable");
  const [userId, setUserId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);

  const [existingContratId, setExistingContratId] = useState(null);
  const [typeContrat, setTypeContrat] = useState("cdi");
  const [dateDebut, setDateDebut] = useState(new Date().toISOString().slice(0, 10));
  const [dateFin, setDateFin] = useState("");
  const [horaireUniforme, setHoraireUniforme] = useState(true);
  const [heureUniformeDebut, setHeureUniformeDebut] = useState("09:00");
  const [heureUniformeFin, setHeureUniformeFin] = useState("17:00");
  const [joursActifs, setJoursActifs] = useState({ lundi: true, mardi: true, mercredi: true, jeudi: true, vendredi: true, samedi: false, dimanche: false });
  const [horairesParJour, setHorairesParJour] = useState(
    Object.fromEntries(JOURS_SEMAINE.map((j) => [j, { heure_debut: "09:00", heure_fin: "17:00" }]))
  );

  const [periodeType, setPeriodeType] = useState("semaine");
  const [periodeValeur, setPeriodeValeur] = useState("");
  const [joursVariables, setJoursVariables] = useState({}); // { [date]: { actif, heure_debut, heure_fin, creneauId? } }

  const resetStableForm = () => {
    setExistingContratId(null);
    setTypeContrat("cdi");
    setDateDebut(new Date().toISOString().slice(0, 10));
    setDateFin("");
    setHoraireUniforme(true);
    setHeureUniformeDebut("09:00");
    setHeureUniformeFin("17:00");
    setJoursActifs({ lundi: true, mardi: true, mercredi: true, jeudi: true, vendredi: true, samedi: false, dimanche: false });
    setHorairesParJour(Object.fromEntries(JOURS_SEMAINE.map((j) => [j, { heure_debut: "09:00", heure_fin: "17:00" }])));
  };

  // Dès qu'un employé est sélectionné (mode horaire stable), on récupère son
  // contrat déjà renseigné s'il existe, pour proposer directement de le
  // modifier ou de le supprimer plutôt que d'en recréer un à l'aveugle
  // (bug corrigé : impossible jusqu'ici de revenir sur un horaire stable
  // déjà enregistré).
  useEffect(() => {
    if (!userId || mode !== "stable") return;
    setLoadingExisting(true);
    api.get(`/contrats/${userId}`)
      .then(({ data }) => {
        if (!data) { resetStableForm(); return; }
        setExistingContratId(data.id);
        setTypeContrat(data.type_contrat);
        setDateDebut(data.date_debut);
        setDateFin(data.date_fin || "");
        const jours = data.horaires_semaine || {};
        setJoursActifs(Object.fromEntries(JOURS_SEMAINE.map((j) => [j, !!jours[j]?.actif])));
        setHorairesParJour(Object.fromEntries(JOURS_SEMAINE.map((j) => [
          j, { heure_debut: jours[j]?.heure_debut || "09:00", heure_fin: jours[j]?.heure_fin || "17:00" },
        ])));
        // Un seul horaire uniforme n'est proposé en coché que si TOUS les
        // jours actifs partagent effectivement les mêmes horaires.
        const actifs = JOURS_SEMAINE.filter((j) => jours[j]?.actif);
        const toutesIdentiques = actifs.length > 0 && actifs.every((j) => jours[j].heure_debut === jours[actifs[0]].heure_debut && jours[j].heure_fin === jours[actifs[0]].heure_fin);
        setHoraireUniforme(toutesIdentiques);
        if (toutesIdentiques) {
          setHeureUniformeDebut(jours[actifs[0]].heure_debut);
          setHeureUniformeFin(jours[actifs[0]].heure_fin);
        }
      })
      .catch(() => resetStableForm())
      .finally(() => setLoadingExisting(false));
  }, [userId, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleJour = (j) => setJoursActifs((prev) => ({ ...prev, [j]: !prev[j] }));

  const joursDeLaPeriode = () => {
    if (!periodeValeur) return [];
    if (periodeType === "semaine") {
      const lundi = new Date(periodeValeur);
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(lundi);
        d.setDate(lundi.getDate() + i);
        return d.toISOString().slice(0, 10);
      });
    }
    const [y, m] = periodeValeur.split("-").map(Number);
    const nbJours = new Date(y, m, 0).getDate();
    return Array.from({ length: nbJours }, (_, i) => `${periodeValeur}-${String(i + 1).padStart(2, "0")}`);
  };

  // Dès qu'un employé ET une période sont choisis (mode horaire variable), on
  // récupère les créneaux déjà renseignés sur cette période pour les
  // pré-cocher — permet de les modifier ou de les décocher pour les
  // supprimer, plutôt que de ne pouvoir qu'en ajouter de nouveaux.
  useEffect(() => {
    if (!userId || mode !== "variable" || !periodeValeur) return;
    const jours = joursDeLaPeriode();
    if (jours.length === 0) return;
    setLoadingExisting(true);
    api.get("/planning", { params: { user_id: userId, date_from: jours[0], date_to: jours[jours.length - 1] } })
      .then(({ data }) => {
        const map = {};
        for (const c of data) {
          map[c.date] = { actif: true, heure_debut: c.heure_debut, heure_fin: c.heure_fin, creneauId: c.id };
        }
        setJoursVariables(map);
      })
      .catch(() => setJoursVariables({}))
      .finally(() => setLoadingExisting(false));
  }, [userId, mode, periodeType, periodeValeur]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitStable = async () => {
    if (!userId) { toast.error("Sélectionnez un employé"); return; }
    if (typeContrat === "cdd" && !dateFin) { toast.error("Indiquez la date de fin du CDD"); return; }
    const horaires_semaine = {};
    for (const j of JOURS_SEMAINE) {
      horaires_semaine[j] = joursActifs[j]
        ? { actif: true, heure_debut: horaireUniforme ? heureUniformeDebut : horairesParJour[j].heure_debut, heure_fin: horaireUniforme ? heureUniformeFin : horairesParJour[j].heure_fin }
        : { actif: false };
    }
    setSubmitting(true);
    try {
      const payload = { user_id: userId, type_contrat: typeContrat, date_debut: dateDebut, date_fin: typeContrat === "cdd" ? dateFin : null, horaires_semaine };
      if (existingContratId) {
        await api.patch(`/contrats/${existingContratId}`, payload);
        toast.success("Horaire stable modifié");
      } else {
        await api.post("/contrats", payload);
        toast.success("Horaire stable enregistré");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteStable = async () => {
    if (!existingContratId) return;
    if (!window.confirm("Supprimer cet horaire stable ?")) return;
    setSubmitting(true);
    try {
      await api.delete(`/contrats/${existingContratId}`);
      toast.success("Horaire stable supprimé");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const submitVariable = async () => {
    if (!userId) { toast.error("Sélectionnez un employé"); return; }
    const jours = joursDeLaPeriode();
    const actifs = jours.filter((j) => joursVariables[j]?.actif);
    const aSupprimer = jours.filter((j) => joursVariables[j]?.creneauId && !joursVariables[j]?.actif);
    if (actifs.length === 0 && aSupprimer.length === 0) { toast.error("Sélectionnez au moins un jour travaillé"); return; }
    setSubmitting(true);
    try {
      for (const j of actifs) {
        const h = joursVariables[j];
        const heure_debut = horaireUniforme ? heureUniformeDebut : (h.heure_debut || "09:00");
        const heure_fin = horaireUniforme ? heureUniformeFin : (h.heure_fin || "17:00");
        if (h.creneauId) {
          await api.patch(`/planning/${h.creneauId}`, { user_id: userId, date: j, heure_debut, heure_fin }, { params: { force: true } });
        } else {
          await api.post("/planning", { user_id: userId, date: j, heure_debut, heure_fin }, { params: { force: true } });
        }
      }
      for (const j of aSupprimer) {
        await api.delete(`/planning/${joursVariables[j].creneauId}`);
      }
      toast.success("Horaires enregistrés");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="renseigner-employe-dialog">
        <DialogHeader><DialogTitle>Renseigner un employé</DialogTitle></DialogHeader>
        <Select value={userId} onValueChange={setUserId}>
          <SelectTrigger data-testid="renseigner-user-select"><SelectValue placeholder="Choisir un employé" /></SelectTrigger>
          <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.prenom} {u.nom}</SelectItem>)}</SelectContent>
        </Select>

        <div className="flex gap-2 my-3">
          <Button size="sm" variant={mode === "stable" ? "default" : "outline"} onClick={() => setMode("stable")} data-testid="renseigner-mode-stable">
            1. Horaire stable
          </Button>
          <Button size="sm" variant={mode === "variable" ? "default" : "outline"} onClick={() => setMode("variable")} data-testid="renseigner-mode-variable">
            2. Horaire variable
          </Button>
        </div>

        {loadingExisting && <p className="text-xs text-muted-foreground mb-2">Chargement des horaires déjà renseignés...</p>}

        {mode === "stable" ? (
          <div className="space-y-3">
            {existingContratId && (
              <p className="text-xs rounded-lg bg-primary/10 text-primary px-3 py-2" data-testid="renseigner-existing-notice">
                Cet employé a déjà un horaire stable renseigné — modifiez-le ci-dessous, ou supprimez-le.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Select value={typeContrat} onValueChange={setTypeContrat}>
                <SelectTrigger className="w-28" data-testid="renseigner-type-contrat"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cdi">CDI</SelectItem><SelectItem value="cdd">CDD</SelectItem></SelectContent>
              </Select>
              <Input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} data-testid="renseigner-date-debut" />
              {typeContrat === "cdd" && <Input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} data-testid="renseigner-date-fin" />}
            </div>
            <p className="text-xs text-muted-foreground">
              {typeContrat === "cdi" ? "CDI : durée du contrat indéfinie, pas de date de fin." : "CDD : indiquez la date de fin du contrat."}
            </p>

            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={horaireUniforme} onCheckedChange={(v) => setHoraireUniforme(!!v)} data-testid="renseigner-horaire-uniforme" />
              Même horaire chaque jour travaillé
            </label>
            {horaireUniforme && (
              <div className="grid grid-cols-2 gap-2">
                <Input type="time" value={heureUniformeDebut} onChange={(e) => setHeureUniformeDebut(e.target.value)} />
                <Input type="time" value={heureUniformeFin} onChange={(e) => setHeureUniformeFin(e.target.value)} />
              </div>
            )}

            <div className="space-y-2">
              {JOURS_SEMAINE.map((j) => (
                <div key={j} className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm w-28 shrink-0">
                    <Checkbox checked={joursActifs[j]} onCheckedChange={() => toggleJour(j)} data-testid={`renseigner-jour-${j}`} />
                    {JOURS_LABELS[j]}
                  </label>
                  {joursActifs[j] && !horaireUniforme && (
                    <div className="grid grid-cols-2 gap-2 flex-1">
                      <Input type="time" value={horairesParJour[j].heure_debut} onChange={(e) => setHorairesParJour((p) => ({ ...p, [j]: { ...p[j], heure_debut: e.target.value } }))} />
                      <Input type="time" value={horairesParJour[j].heure_fin} onChange={(e) => setHorairesParJour((p) => ({ ...p, [j]: { ...p[j], heure_fin: e.target.value } }))} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Select value={periodeType} onValueChange={(v) => { setPeriodeType(v); setPeriodeValeur(""); setJoursVariables({}); }}>
                <SelectTrigger className="w-32" data-testid="renseigner-periode-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="semaine">Semaine</SelectItem><SelectItem value="mois">Mois</SelectItem></SelectContent>
              </Select>
              {periodeType === "semaine" ? (
                <Input type="date" value={periodeValeur} onChange={(e) => setPeriodeValeur(e.target.value)} data-testid="renseigner-periode-semaine" />
              ) : (
                <Input type="month" value={periodeValeur} onChange={(e) => setPeriodeValeur(e.target.value)} data-testid="renseigner-periode-mois" />
              )}
            </div>
            {periodeValeur && Object.values(joursVariables).some((v) => v?.creneauId) && (
              <p className="text-xs rounded-lg bg-primary/10 text-primary px-3 py-2" data-testid="renseigner-variable-existing-notice">
                Des créneaux sont déjà renseignés sur cette période (pré-cochés ci-dessous) — modifiez leurs horaires,
                ou décochez un jour pour supprimer son créneau.
              </p>
            )}

            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={horaireUniforme} onCheckedChange={(v) => setHoraireUniforme(!!v)} data-testid="renseigner-variable-horaire-uniforme" />
              Même horaire chaque jour travaillé
            </label>
            {horaireUniforme && (
              <div className="grid grid-cols-2 gap-2">
                <Input type="time" value={heureUniformeDebut} onChange={(e) => setHeureUniformeDebut(e.target.value)} />
                <Input type="time" value={heureUniformeFin} onChange={(e) => setHeureUniformeFin(e.target.value)} />
              </div>
            )}

            {periodeValeur && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {joursDeLaPeriode().map((j) => {
                  const d = new Date(j);
                  const actif = !!joursVariables[j]?.actif;
                  return (
                    <div key={j} className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-sm w-36 shrink-0">
                        <Checkbox checked={actif} onCheckedChange={(v) => setJoursVariables((p) => ({ ...p, [j]: { ...p[j], actif: !!v } }))} data-testid={`renseigner-variable-jour-${j}`} />
                        {d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}
                        {joursVariables[j]?.creneauId && <span className="text-[10px] text-muted-foreground">(existant)</span>}
                      </label>
                      {actif && !horaireUniforme && (
                        <div className="grid grid-cols-2 gap-2 flex-1">
                          <Input type="time" value={joursVariables[j]?.heure_debut || "09:00"} onChange={(e) => setJoursVariables((p) => ({ ...p, [j]: { ...p[j], heure_debut: e.target.value } }))} />
                          <Input type="time" value={joursVariables[j]?.heure_fin || "17:00"} onChange={(e) => setJoursVariables((p) => ({ ...p, [j]: { ...p[j], heure_fin: e.target.value } }))} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          {mode === "stable" && existingContratId && (
            <Button variant="destructive" onClick={deleteStable} disabled={submitting} data-testid="renseigner-delete-stable">
              Supprimer
            </Button>
          )}
          <Button onClick={mode === "stable" ? submitStable : submitVariable} disabled={submitting} data-testid="renseigner-submit">
            {submitting ? "Enregistrement..." : existingContratId && mode === "stable" ? "Modifier" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CalendrierTab({ users }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const [data, setData] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [renseignerOpen, setRenseignerOpen] = useState(false);
  const [shops, setShops] = useState([]);
  const [shopFilter, setShopFilter] = useState("__all__");

  useEffect(() => {
    api.get("/shops").then((r) => setShops(r.data)).catch(() => setShops([]));
  }, []);

  const today = new Date();
  const viewDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const mois = `${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, "0")}`;
  const monthLabel = viewDate.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  const load = async () => {
    try {
      const params = { mois };
      if (shopFilter !== "__all__") params.shop_id = shopFilter;
      const { data } = await api.get("/planning/calendrier", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [mois, shopFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const cellColor = (info) => {
    if (!info) return "";
    if (info.statut === "conge") return CONGE_COLORS[info.conge?.type_conge] || "bg-gray-400";
    return STATUT_COLORS[info.statut] || "";
  };

  if (!data) return <p className="text-sm text-muted-foreground">Chargement...</p>;

  const isWeekend = (isoDate) => [0, 6].includes(new Date(isoDate).getDay());

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMonthOffset((m) => m - 1)} data-testid="calendrier-prev-month"><ChevronLeft size={16} /></Button>
          <p className="font-semibold capitalize min-w-[160px] text-center" data-testid="calendrier-month-label">{monthLabel}</p>
          <Button variant="outline" size="icon" onClick={() => setMonthOffset((m) => m + 1)} data-testid="calendrier-next-month"><ChevronRight size={16} /></Button>
        </div>
        <div className="flex items-center gap-2">
          <Select value={shopFilter} onValueChange={setShopFilter}>
            <SelectTrigger className="w-52" data-testid="calendrier-shop-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Toutes les boutiques</SelectItem>
              {shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button className="gap-1.5" onClick={() => setRenseignerOpen(true)} data-testid="renseigner-employe-button">
            <Plus size={14} />Renseigner employé
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mb-4 text-xs">
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-500" />Présent</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-sky-400" />Prévu</div>
        <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-red-500" />Absent (non pointé)</div>
        {Object.entries(TYPE_CONGE_LABELS).map(([key, label]) => (
          <div key={key} className="flex items-center gap-1.5"><span className={`inline-block w-3 h-3 rounded-sm ${CONGE_COLORS[key]}`} />{label}</div>
        ))}
      </div>

      {data.employes.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="calendrier-empty">Aucun employé dans cette boutique.</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="border-collapse text-xs w-full" data-testid="calendrier-table">
            <thead>
              <tr className="border-b">
                <th className="sticky left-0 bg-background text-left px-3 py-2 font-medium whitespace-nowrap">Employé</th>
                {data.jours.map((jourIso) => {
                  const d = new Date(jourIso);
                  return (
                    <th key={jourIso} className={`w-7 px-0 py-1 text-center font-normal ${isWeekend(jourIso) ? "bg-muted/60 text-muted-foreground" : "text-muted-foreground"}`}>
                      <div>{d.getDate()}</div>
                      <div className="text-[9px] opacity-70">{WEEKDAY_LETTERS[(d.getDay() + 6) % 7]}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.employes.map((emp) => (
                <tr key={emp.user_id} className="border-b last:border-b-0" data-testid={`calendrier-row-${emp.user_id}`}>
                  <td className="sticky left-0 bg-background px-3 py-1.5 whitespace-nowrap font-medium">
                    {emp.user_nom}
                    <span className="block text-[10px] text-muted-foreground font-normal">
                      {emp.poste}{emp.poste && emp.shop_nom ? " — " : ""}{shopFilter === "__all__" ? emp.shop_nom : ""}
                    </span>
                  </td>
                  {data.jours.map((jourIso) => {
                    const info = emp.jours[jourIso];
                    return (
                      <td key={jourIso} className="w-7 h-8 text-center p-0.5">
                        <button
                          className={`w-full h-full rounded-sm transition-transform hover:scale-110 ${cellColor(info)} ${!cellColor(info) ? "hover:bg-accent" : ""}`}
                          onClick={() => setSelectedCell({ user: users.find((u) => u.id === emp.user_id) || { id: emp.user_id, prenom: emp.user_nom, nom: "" }, jour: jourIso, info })}
                          data-testid={`calendrier-cell-${emp.user_id}-${jourIso}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedCell && <JourDetailDialog cell={selectedCell} onClose={() => setSelectedCell(null)} onChanged={load} />}
      {renseignerOpen && <RenseignerEmployeDialog users={users} onClose={() => setRenseignerOpen(false)} onSaved={load} />}
    </div>
  );
}

function CommissionsTab() {
  const [recap, setRecap] = useState([]);
  const [dateFrom, setDateFrom] = useState(`${new Date().toISOString().slice(0, 7)}-01`);
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = `${dateFrom}T00:00:00`;
      if (dateTo) params.date_to = `${dateTo}T23:59:59`;
      const { data } = await api.get("/commissions/recap", { params });
      setRecap(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3">
        Calculé automatiquement depuis l'historique réel des ventes (jamais saisi à la main) — le taux de chaque
        employé se règle sur sa fiche utilisateur.
      </p>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Du</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="commissions-date-from" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Au</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="commissions-date-to" />
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]" data-testid="commissions-table">
          <thead className="bg-secondary text-left">
            <tr><th className="p-3">Vendeur</th><th className="p-3">CA TTC</th><th className="p-3">Documents</th><th className="p-3">Taux</th><th className="p-3">Commission</th></tr>
          </thead>
          <tbody>
            {recap.length === 0 && (
              <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">Aucune vente sur cette période.</td></tr>
            )}
            {recap.map((r) => (
              <tr key={r.vendeur_id} className="border-t border-border" data-testid={`commission-row-${r.vendeur_id}`}>
                <td className="p-3">{r.vendeur_nom || "?"}</td>
                <td className="p-3">{r.total_ttc.toFixed(2)} €</td>
                <td className="p-3 text-muted-foreground">{r.nb_documents}</td>
                <td className="p-3">{r.commission_percent}%</td>
                <td className="p-3 font-semibold">{r.commission_amount.toFixed(2)} €</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ObjectifsTab({ users }) {
  const [objectifs, setObjectifs] = useState([]);
  const [mois, setMois] = useState(new Date().toISOString().slice(0, 7));
  const [addOpen, setAddOpen] = useState(false);
  const [addUserId, setAddUserId] = useState("__shop__");
  const [addMontant, setAddMontant] = useState("");
  const [addSubmitting, setAddSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/objectifs", { params: { mois } });
      setObjectifs(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [mois]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitAdd = async () => {
    const montant = Number(addMontant);
    if (!montant || montant <= 0) { toast.error("Indiquez un montant cible positif"); return; }
    setAddSubmitting(true);
    try {
      await api.post("/objectifs", {
        mois, montant_cible: montant,
        user_id: addUserId === "__shop__" ? null : addUserId,
      });
      toast.success("Objectif créé");
      setAddOpen(false);
      setAddMontant("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAddSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/objectifs/${id}`);
      toast.success("Objectif supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Mois</label>
          <Input type="month" value={mois} onChange={(e) => setMois(e.target.value)} data-testid="objectifs-mois-select" />
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setAddOpen(true)} data-testid="objectifs-add-button">
          <Plus size={14} />Nouvel objectif
        </Button>
      </div>
      <div className="space-y-2">
        {objectifs.length === 0 && <p className="text-sm text-muted-foreground">Aucun objectif défini pour ce mois.</p>}
        {objectifs.map((o) => (
          <div key={o.id} className="rounded-xl border border-border bg-card p-4" data-testid={`objectif-row-${o.id}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium">
                {o.user_id ? (users.find((u) => u.id === o.user_id)?.prenom + " " + users.find((u) => u.id === o.user_id)?.nom) : "Objectif boutique (tous vendeurs)"}
              </p>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">{o.montant_realise.toFixed(0)} € / {o.montant_cible.toFixed(0)} €</span>
                <button onClick={() => remove(o.id)} className="text-destructive" data-testid={`objectif-delete-${o.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
              <div className={`h-full ${o.progression_percent >= 100 ? "bg-emerald-500" : "bg-primary"}`} style={{ width: `${o.progression_percent}%` }} />
            </div>
          </div>
        ))}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvel objectif — {mois}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={addUserId} onValueChange={setAddUserId}>
              <SelectTrigger data-testid="objectifs-add-user"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__shop__">Objectif de boutique (tous vendeurs)</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.prenom} {u.nom}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="number" min="1" placeholder="Montant cible (€)" value={addMontant} onChange={(e) => setAddMontant(e.target.value)} data-testid="objectifs-add-montant" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Annuler</Button>
            <Button onClick={submitAdd} disabled={addSubmitting} data-testid="objectifs-add-submit">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function DossiersEmployesTab({ users }) {
  const [selectedUser, setSelectedUser] = useState(null);

  if (selectedUser) {
    return <DossierEmploye user={selectedUser} onBack={() => setSelectedUser(null)} />;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground mb-2">Documents, entretiens et alertes d'échéance par employé.</p>
      {users.map((u) => (
        <button
          key={u.id}
          onClick={() => setSelectedUser(u)}
          className="w-full flex items-center justify-between rounded-lg border border-border bg-card p-3 text-sm hover:bg-accent transition-colors text-left"
          data-testid={`dossier-employe-${u.id}`}
        >
          <span>{u.prenom} {u.nom} — <span className="text-muted-foreground">{u.poste}</span></span>
          <ChevronRight size={16} className="text-muted-foreground" />
        </button>
      ))}
    </div>
  );
}

function DossierEmploye({ user, onBack }) {
  const [documents, setDocuments] = useState([]);
  const [entretiens, setEntretiens] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const fileInputRef = useRef(null);

  const [entretienOpen, setEntretienOpen] = useState(false);
  const [entretienType, setEntretienType] = useState("Entretien annuel");
  const [entretienDate, setEntretienDate] = useState(new Date().toISOString().slice(0, 10));
  const [entretienNotes, setEntretienNotes] = useState("");

  const [alerteOpen, setAlerteOpen] = useState(false);
  const [alerteLibelle, setAlerteLibelle] = useState("");
  const [alerteDate, setAlerteDate] = useState("");

  const load = () => {
    api.get(`/rh/employes/${user.id}/documents`).then((r) => setDocuments(r.data)).catch(() => setDocuments([]));
    api.get(`/rh/employes/${user.id}/entretiens`).then((r) => setEntretiens(r.data)).catch(() => setEntretiens([]));
    api.get("/rh/alertes", { params: { user_id: user.id } }).then((r) => setAlertes(r.data)).catch(() => setAlertes([]));
  };
  useEffect(() => { load(); }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadDocument = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("libelle", file.name);
    try {
      await api.post(`/rh/employes/${user.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document ajouté");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      e.target.value = "";
    }
  };

  const deleteDocument = async (id) => {
    try {
      await api.delete(`/rh/employes/documents/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitEntretien = async () => {
    if (!entretienNotes.trim()) { toast.error("Les notes sont obligatoires"); return; }
    try {
      await api.post(`/rh/employes/${user.id}/entretiens`, { date_entretien: entretienDate, type_entretien: entretienType, notes: entretienNotes });
      toast.success("Entretien enregistré");
      setEntretienOpen(false);
      setEntretienNotes("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteEntretien = async (id) => {
    try {
      await api.delete(`/rh/employes/entretiens/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitAlerte = async () => {
    if (!alerteLibelle.trim() || !alerteDate) { toast.error("Libellé et date obligatoires"); return; }
    try {
      await api.post("/rh/alertes", { user_id: user.id, libelle: alerteLibelle, date_echeance: alerteDate });
      toast.success("Alerte créée");
      setAlerteOpen(false);
      setAlerteLibelle(""); setAlerteDate("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteAlerte = async (id) => {
    try {
      await api.delete(`/rh/alertes/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <Button variant="ghost" size="sm" className="gap-1.5 mb-3" onClick={onBack}><ChevronLeft size={14} />Retour à la liste</Button>
      <p className="font-heading font-semibold text-lg mb-4">{user.prenom} {user.nom}</p>

      <div className="rounded-xl border border-border bg-card p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading font-semibold">Documents</p>
          <input ref={fileInputRef} type="file" className="hidden" onChange={uploadDocument} data-testid="dossier-file-input" />
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => fileInputRef.current?.click()} data-testid="dossier-upload-button">
            <Upload size={13} />Ajouter un document
          </Button>
        </div>
        {documents.length === 0 && <p className="text-sm text-muted-foreground">Aucun document.</p>}
        <div className="space-y-1.5">
          {documents.map((d) => (
            <div key={d.id} className="flex items-center justify-between text-sm rounded-lg border border-border px-3 py-2" data-testid={`dossier-doc-${d.id}`}>
              <a href={`${UPLOADS_BASE}${d.url}`} target="_blank" rel="noreferrer" className="text-primary truncate">{d.libelle}</a>
              <button onClick={() => deleteDocument(d.id)} className="text-destructive shrink-0"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading font-semibold">Historique d'entretiens</p>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEntretienOpen(true)} data-testid="dossier-add-entretien"><Plus size={13} />Ajouter</Button>
        </div>
        {entretiens.length === 0 && <p className="text-sm text-muted-foreground">Aucun entretien enregistré.</p>}
        <div className="space-y-2">
          {entretiens.map((ent) => (
            <div key={ent.id} className="text-sm rounded-lg border border-border p-3" data-testid={`dossier-entretien-${ent.id}`}>
              <div className="flex items-center justify-between mb-1">
                <p className="font-medium">{ent.type_entretien} — {ent.date_entretien}</p>
                <button onClick={() => deleteEntretien(ent.id)} className="text-destructive"><Trash2 size={13} /></button>
              </div>
              <p className="text-muted-foreground">{ent.notes}</p>
              <p className="text-xs text-muted-foreground mt-1">Par {ent.created_by_nom}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="font-heading font-semibold">Alertes d'échéance</p>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setAlerteOpen(true)} data-testid="dossier-add-alerte"><Plus size={13} />Ajouter</Button>
        </div>
        {alertes.length === 0 && <p className="text-sm text-muted-foreground">Aucune alerte.</p>}
        <div className="space-y-1.5">
          {alertes.map((a) => (
            <div key={a.id} className={`flex items-center justify-between text-sm rounded-lg border px-3 py-2 ${a.jours_restants < 0 ? "border-destructive/40" : a.jours_restants <= 15 ? "border-amber-500/40" : "border-border"}`} data-testid={`dossier-alerte-${a.id}`}>
              <span>{a.libelle} — {a.date_echeance} ({a.jours_restants >= 0 ? `dans ${a.jours_restants} j.` : `dépassée depuis ${-a.jours_restants} j.`})</span>
              <button onClick={() => deleteAlerte(a.id)} className="text-destructive shrink-0"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={entretienOpen} onOpenChange={setEntretienOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvel entretien</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={entretienType} onChange={(e) => setEntretienType(e.target.value)} placeholder="Type d'entretien" data-testid="dossier-entretien-type" />
            <Input type="date" value={entretienDate} onChange={(e) => setEntretienDate(e.target.value)} data-testid="dossier-entretien-date" />
            <Textarea value={entretienNotes} onChange={(e) => setEntretienNotes(e.target.value)} placeholder="Notes" rows={4} data-testid="dossier-entretien-notes" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEntretienOpen(false)}>Annuler</Button>
            <Button onClick={submitEntretien} data-testid="dossier-entretien-submit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={alerteOpen} onOpenChange={setAlerteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle alerte d'échéance</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={alerteLibelle} onChange={(e) => setAlerteLibelle(e.target.value)} placeholder="Ex. Fin de période d'essai" data-testid="dossier-alerte-libelle" />
            <Input type="date" value={alerteDate} onChange={(e) => setAlerteDate(e.target.value)} data-testid="dossier-alerte-date" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAlerteOpen(false)}>Annuler</Button>
            <Button onClick={submitAlerte} data-testid="dossier-alerte-submit">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PaieTab() {
  const [mois, setMois] = useState(new Date().toISOString().slice(0, 7));
  const [recap, setRecap] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/paie/recap", { params: { mois } });
      setRecap(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [mois]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = () => {
    window.open(`${api.defaults.baseURL}/paie/export.csv?mois=${mois}`, "_blank");
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Mois</label>
          <Input type="month" value={mois} onChange={(e) => setMois(e.target.value)} data-testid="paie-mois-select" />
        </div>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={download} data-testid="paie-export-button">
          <Download size={14} />Exporter en CSV
        </Button>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]" data-testid="paie-table">
          <thead className="bg-secondary text-left">
            <tr>
              <th className="p-3">Employé</th><th className="p-3">Heures</th><th className="p-3">Absences</th>
              <th className="p-3">CA réalisé</th><th className="p-3">Commission</th>
            </tr>
          </thead>
          <tbody>
            {recap.length === 0 && (
              <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">Aucune donnée pour ce mois.</td></tr>
            )}
            {recap.map((r) => (
              <tr key={r.user_id} className="border-t border-border" data-testid={`paie-row-${r.user_id}`}>
                <td className="p-3">{r.nom}</td>
                <td className="p-3">{r.heures_travaillees.toFixed(2)} h{r.heures_a_valider > 0 && <span className="ml-1 text-xs text-red-600" title="Heures hors horaire en attente de validation (non comptées)">(+{r.heures_a_valider.toFixed(2)} h à valider)</span>}</td>
                <td className="p-3">{r.nb_jours_absence}</td>
                <td className="p-3">{r.ca_realise.toFixed(2)} €</td>
                <td className="p-3 font-semibold">{r.commission_amount.toFixed(2)} €</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
